import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import type { IntegrationExecution, IntegrationExecutionDetail } from 'services/api/integrations.api';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import { getExecutionTimeColor } from '../utils';

interface ExecutionDetailDrawerProps {
  execution: IntegrationExecution | null;
  detail: IntegrationExecutionDetail | null;
  isDetailLoading: boolean;
  open: boolean;
  onClose: () => void;
}

// Wide enough for JSON payloads to breathe; falls back to full width on narrow screens.
const DRAWER_WIDTH = 'min(640px, 100vw)';

const formatJson = (data: unknown): string => {
  if (data === null || data === undefined || data === '') return '';
  if (typeof data === 'string') {
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      return data;
    }
  }
  return JSON.stringify(data, null, 2);
};

const copyToClipboard = (text: string): Promise<void> =>
  navigator.clipboard.writeText(text).catch(() => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  });

const MetadataItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      backgroundColor: SURFACE_CONTAINER_LOW,
      borderRadius: RADIUS_ITEM,
      px: 1.5,
      py: 1,
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Jost',
        fontWeight: 600,
        color: alpha(NAVY, 0.5),
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: '0.6rem',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontFamily: 'Jost', color: NAVY, fontSize: '0.85rem', fontWeight: 500 }}>{children}</Typography>
  </Box>
);

const JsonPanel: React.FC<{ label: string; data: unknown; loading?: boolean }> = ({ label, data, loading }) => {
  const { notifyCopied } = useFeedback();
  const jsonString = formatJson(data);
  const hasData = jsonString !== '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography
          sx={{
            fontFamily: 'Jost',
            fontWeight: 600,
            color: alpha(NAVY, 0.55),
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.65rem',
          }}
        >
          {label}
        </Typography>
        {hasData && !loading && (
          <Tooltip title="Copiar" arrow>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(jsonString).then(() => notifyCopied('Copiado'))}
              sx={{
                backgroundColor: alpha(NAVY, 0.06),
                color: NAVY,
                '&:hover': { backgroundColor: alpha(NAVY, 0.12) },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: SURFACE_CONTAINER_LOW,
          borderRadius: RADIUS_ITEM,
          p: 2,
          maxHeight: '40vh',
          overflow: 'auto',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} sx={{ color: alpha(NAVY, 0.5) }} />
            <Typography sx={{ fontFamily: 'Jost', color: alpha(NAVY, 0.5), fontStyle: 'italic', fontSize: '0.85rem' }}>
              Cargando…
            </Typography>
          </Box>
        ) : hasData ? (
          <Typography
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.72rem',
              lineHeight: 1.5,
              color: NAVY,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              m: 0,
            }}
          >
            {jsonString}
          </Typography>
        ) : (
          <Typography sx={{ fontFamily: 'Jost', color: alpha(NAVY, 0.5), fontStyle: 'italic', fontSize: '0.85rem' }}>
            Sin datos
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ExecutionDetailDrawer: React.FC<ExecutionDetailDrawerProps> = ({
  execution,
  detail,
  isDetailLoading,
  open,
  onClose,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderTopLeftRadius: RADIUS_CARD,
            borderBottomLeftRadius: RADIUS_CARD,
          },
        },
      }}
    >
      {execution && (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Chip
                label={execution.status}
                color={execution.status === 'success' ? 'success' : 'error'}
                size="small"
              />
              <Typography sx={{ fontFamily: 'Jost', fontWeight: 700, color: NAVY, fontSize: '1.1rem' }}>
                #{execution.id}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: NAVY }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Method + endpoint */}
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOW,
              borderRadius: RADIUS_ITEM,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: NAVY, wordBreak: 'break-all' }}>
              {execution.method && `${execution.method} `}
              {execution.endpoint}
            </Typography>
          </Box>

          {/* Metadata grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
            <MetadataItem label="Tiempo de ejecución">
              <Typography
                component="span"
                sx={{ fontFamily: 'Jost', fontWeight: 500, fontSize: '0.85rem' }}
                color={getExecutionTimeColor(execution.executionTime)}
              >
                {execution.executionTime}ms
              </Typography>
            </MetadataItem>
            <MetadataItem label="Timestamp">{new Date(execution.createdAt).toLocaleString()}</MetadataItem>
            {execution.statusCode !== null && (
              <MetadataItem label="Código HTTP">
                <Typography
                  component="span"
                  sx={{ fontFamily: 'Jost', fontWeight: 600, fontSize: '0.85rem' }}
                  color={execution.statusCode >= 400 ? 'error.main' : 'success.main'}
                >
                  {execution.statusCode}
                </Typography>
              </MetadataItem>
            )}
            <MetadataItem label="Tipo">{execution.type}</MetadataItem>
            <MetadataItem label="Business Unit ID">{execution.businessUnitId ?? '—'}</MetadataItem>
            {execution.clientId !== null && <MetadataItem label="Client ID">{execution.clientId}</MetadataItem>}
          </Box>

          {/* Error block */}
          {execution.status === 'error' && execution.error && (
            <Box
              sx={{
                backgroundColor: (theme) => theme.palette.error.light,
                borderRadius: RADIUS_ITEM,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Jost',
                  fontWeight: 600,
                  color: 'error.main',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.6rem',
                  mb: 0.25,
                }}
              >
                Error
              </Typography>
              <Typography
                sx={{ fontFamily: 'monospace', color: 'error.main', fontSize: '0.78rem', wordBreak: 'break-word' }}
              >
                {execution.error}
              </Typography>
            </Box>
          )}

          {/* JSON payloads — fetched lazily; the list response omits them. */}
          <JsonPanel label="Request body" data={detail?.input} loading={isDetailLoading && !detail} />
          <JsonPanel label="Respuesta" data={detail?.output} loading={isDetailLoading && !detail} />
          {detail?.headers != null && <JsonPanel label="Headers" data={detail.headers} />}
        </Box>
      )}
    </Drawer>
  );
};

export default ExecutionDetailDrawer;
